import {
    BadgeCheck,
    BarChart3,
    CalendarClock,
    Car,
    ClipboardCheck,
    ClipboardList,
    CreditCard,
    FilePlus,
    FileText,
    Home,
    LayoutDashboard,
    LayoutGrid,
    Landmark,
    MessageCircle,
    ListChecks,
    Package,
    Receipt,
    Settings as SettingsIcon,
    ShieldCheck,
    Sparkles,
    ShoppingCart,
    Truck,
    UserCog,
    Users,
    Wallet,
    Wrench,
    Award,
} from 'lucide-react';
import { usePermissions, SETTINGS_PERMISSIONS } from '../../hooks/usePermissions';
import { useEnterpriseConfigContext } from '../context/EnterpriseConfigContext';
import { useAllowedDashboardTabs } from '../../pages/dashboards/dashboardTabs';
import { isAiChatEnabled } from '../../services/aiChat';

/**
 * Single source of truth for the app shell navigation.
 *
 * Mirrors, one-for-one, the permission gates that used to live inline in
 * `ModernSidebar.js` — only the *presentation* changed (flat grouped
 * sections instead of collapsible submenus, per DESIGN-SYSTEM.md §3.1).
 * No permission string, path, or gating condition was altered.
 *
 * Reused by: Sidebar (render), Topbar breadcrumb (path -> label lookup) and
 * the command palette (static "Navegar" results) so the three surfaces never
 * drift apart — see DESIGN-SYSTEM.md §5.10.
 */
export function useNavSections() {
    const { hasPermission, hasAnyPermission, hasModule, isSysAdmin, trialExpired } = usePermissions();
    // `os_use_checklist` (Configurações > "Usa checklist de inspeção") — os
    // 2 itens de menu "Checklists" (Cadastros > Checklists/templates e
    // Relatórios > Checklists) só fazem sentido quando o módulo de
    // checklist de inspeção está ligado; sem isso a permissão de perfil
    // sozinha deixava os itens visíveis mesmo com a config desligada.
    const { settings } = useEnterpriseConfigContext();
    const checklistEnabled = Boolean(settings?.os_use_checklist);
    const vehicleEnabled = Boolean(settings?.os_use_vehicle);
    const dashboardTabs = useAllowedDashboardTabs();

    const hasAnyReportPermission =
        hasPermission('report.stock') ||
        hasPermission('report.inventory') ||
        hasPermission('report.sale') ||
        hasPermission('report.customer') ||
        hasPermission('report.product') ||
        hasPermission('report.serviceorder') ||
        hasPermission('report.vehicle') ||
        (checklistEnabled && hasPermission('report.checklist')) ||
        hasPermission('report.cashflow') ||
        hasPermission('dashboard.sale');

    const sections = [
        {
            id: 'principal',
            label: 'Principal',
            items: [
                ...(hasPermission('home.view') ? [{ id: 'home', label: 'Início', path: '/home', icon: Home }] : []),
                // Hub único (chips Vendas/Ordens/Veículos/Orçamentos); aparece com ao menos uma aba permitida.
                ...(dashboardTabs.length > 0 ? [{ id: 'dashboards', label: 'Dashboards', path: '/dashboards', icon: LayoutDashboard }] : []),
                // Assistente de IA: página em tela cheia (/chat). O painel lateral
                // continua pelo ícone da topbar. Só existe com a flag ligada
                // (REACT_APP_AI_ENABLED).
                // Também depende do módulo `ai_assistant` do plano (embutido em hasPermission)
                // e da permissão de perfil `ai.use`.
                ...(isAiChatEnabled() && hasPermission('ai.use') ? [{ id: 'ai-assistant', label: 'Assistente', path: '/chat', badge: 'Novo', icon: Sparkles }] : []),
            ],
        },
        {
            id: 'vendas',
            label: 'Vendas',
            items: [
                ...(hasPermission('sale.create') ? [{ id: 'sale-new', label: 'Vender', path: '/sale', icon: Receipt }] : []),
                ...(hasPermission('sale.view') ? [{ id: 'sale-list', label: 'Minhas Vendas', path: '/sales', icon: ShoppingCart }] : []),
            ],
        },
        {
            id: 'ordens',
            label: 'Ordens de Serviço',
            items: [
                ...(hasPermission('serviceorder.create') ? [{ id: 'order-new', label: 'Gerar Ordem', path: '/serviceorder/generate', icon: FilePlus }] : []),
                ...(hasPermission('serviceorder.view') ? [{ id: 'order-list', label: 'Minhas Ordens', path: '/serviceorders', icon: ClipboardList }] : []),
                // Recorrência depende de veículo: só aparece com "Usa veículo" ligado.
                // A tela consome `/vehicles/recurring-services/*`, protegido por `serviceorder.upcoming`.
                ...(vehicleEnabled && hasPermission('serviceorder.upcoming') ?[{ id: 'order-upcoming', label: 'Próximos Serviços', path: '/serviceorders/upcoming', icon: CalendarClock }] : []),
            ],
        },
        {   
            id: 'orcamentos',
            label: 'Orçamentos',
            items: [
                // hasPermission já exige o módulo `quote` do plano (ver getModuleForPermission).
                ...(hasPermission('servicequote.create') ? [{ id: 'quote-new', label: 'Gerar Orçamento', path: '/servicequotes/generate', icon: FilePlus }] : []),
                ...(hasPermission('servicequote.view') ? [{ id: 'quote-list', label: 'Meus Orçamentos', path: '/servicequotes', icon: FileText }] : [])
            ],
        },
        {
            id: 'relatorios',
            label: 'Relatórios',     
            hidden: !hasAnyReportPermission,      
            items: [
                ...(hasPermission('report.sale') ? [{ id: 'sales-report', label: 'Vendas', path: '/reports/sales', icon: Receipt }] : []),
                ...(hasPermission('report.serviceorder') ? [{ id: 'orderservice-report', label: 'Ordens de Serviço', path: '/reports/serviceorder', icon: ClipboardList }] : []),
                ...(hasPermission('report.product') ? [{ id: 'product-report', label: 'Produtos', path: '/reports/products', icon: ShoppingCart }] : []),
                ...(hasPermission('report.stock') ? [{ id: 'stock-report', label: 'Estoque', path: '/reports/stock', icon: Package }] : []),
                ...(checklistEnabled && hasPermission('report.checklist') ? [{ id: 'checklist-report', label: 'Checklists', path: '/reports/checklist', icon: ListChecks }] : []),
                ...(hasPermission('report.inventory') ? [{ id: 'inventory-report', label: 'Inventário', path: '/reports/inventory', icon: ClipboardCheck }] : []),
                ...(hasPermission('report.cashflow') ? [{ id: 'cash-flow-report', label: 'Fluxo de Caixa', path: '/reports/cash-flow', icon: BarChart3 }] : []),
                ...(hasPermission('report.customer') ? [{ id: 'customer-report', label: 'Clientes', path: '/reports/customers', icon: UserCog }] : []),
                ...(vehicleEnabled && hasPermission('report.vehicle') ? [{ id: 'vehicle-report', label: 'Veículos', path: '/reports/vehicles', icon: Car }] : [])

            ],
        },
        {
            id: 'financeiro',
            label: 'Financeiro',
            items: [
                // Compras pertence ao módulo `product` (o gate vem do mapeamento de `/products*` em usePermissions).
                ...(hasPermission('purchase.view') ? [{ id: 'purchases', label: 'Compras', path: '/purchases', icon: ShoppingCart }] : []),
                // ...(hasPermission('accounts.payable.view') ? [{ id: 'transactions', label: 'Lançamentos', path: '/transactions', icon: Landmark }] : []),
                ...(hasPermission('accounts.payable.view') ? [{ id: 'accounts-payable', label: 'Contas a pagar', path: '/accounts/payable', icon: Wallet }] : []),
                ...(hasPermission('accounts.receivable.view') ? [{ id: 'accounts-receivable', label: 'Contas a receber', path: '/accounts/receivable', icon: Receipt }] : []),
                ...(hasModule('financial') && hasPermission('serviceorder.bill') ? [{ id: 'os-billing', label: 'Faturamento de OS', path: '/billing/serviceorders', icon: Receipt }] : []),
                ...(hasPermission('cashregister.view') ? [{ id: 'cashregisters', label: 'Caixas', path: '/cashregisters', icon: Wallet }] : []),
                ...(hasPermission('cardmachine.view') ? [{ id: 'cardmachines', label: 'Maquininhas', path: '/cardmachines', icon: CreditCard }] : []),
            ],
        },
        
        {
            id: 'cadastros',
            label: 'Cadastros',
            items: [
                ...(hasPermission('product.view') ? [{ id: 'products', label: 'Produtos', path: '/products', icon: Package }] : []),
                ...(hasPermission('inventory.view') ? [{ id: 'inventory', label: 'Inventário', path: '/inventory', icon: ClipboardCheck }] : []),
                // Compras: usa as permissões existentes de produto (Incluir + Repor Estoque); não há permissão própria seedada.
                ...(hasPermission('category.view') ? [{ id: 'categories', label: 'Categorias', path: '/categories', icon: LayoutGrid }] : []),
                ...(hasPermission('customer.view') ? [{ id: 'customers', label: 'Clientes', path: '/customers', icon: Users }] : []),
                ...(vehicleEnabled && hasPermission('vehicle.view') ? [{ id: 'vehicles', label: 'Veículos', path: '/vehicles', icon: Car }] : []),
                ...(hasPermission('service.view') ? [{ id: 'services', label: 'Serviços', path: '/services', icon: Wrench }] : []),
                ...(hasPermission('supplier.view') ? [{ id: 'suppliers', label: 'Fornecedores', path: '/suppliers', icon: Truck }] : []),
                ...(checklistEnabled && hasPermission('checklist.view') ? [{ id: 'checklists', label: 'Checklists', path: '/vehicle_checklist', icon: ListChecks }] : []),
            ],
        },
        {
            id: 'sistema',
            label: 'Sistema',
            items: [
                ...(hasPermission('user.view') ? [{ id: 'users', label: 'Usuários', path: '/users', icon: UserCog }] : []),
                ...(hasPermission('profile.view') ? [{ id: 'profiles', label: 'Perfis', path: '/profiles', icon: ShieldCheck }] : []),
                ...(hasAnyPermission(SETTINGS_PERMISSIONS) ? [{ id: 'configuration', label: 'Configurações', path: '/settings', icon: SettingsIcon }] : []),
                ...(hasPermission('audit.view') ? [{ id: 'auditlog', label: 'Auditoria', path: '/audit/logs', icon: FileText }] : []),
            ],
        },
        {
            id: 'admin',
            label: 'Administração',
            hidden: !isSysAdmin(),
            items: isSysAdmin() ? [
                { id: 'admin-permissions', label: 'Permissões', path: '/admin/permissions', icon: ShieldCheck },
                { id: 'admin-subscriptions', label: 'Assinaturas', path: '/admin/subscriptions', icon: BadgeCheck },
                { id: 'admin-plans', label: 'Planos', path: '/admin/plans', icon: Award },
                { id: 'admin-configuration', label: 'Configurações', path: '/admin/configuration', icon: SettingsIcon },
                { id: 'admin-whatsapp', label: 'WhatsApp do Sistema', path: '/admin/whatsapp', icon: MessageCircle },
            ] : [],
        },
    ];

    // Teste grátis encerrado (e não sys_admin): só sobra o caminho para assinar.
    if (trialExpired && !isSysAdmin()) {
        return [{
            id: 'conta',
            label: 'Conta',
            items: [{ id: 'plans', label: 'Planos', path: '/plans', icon: Award }],
        }];
    }

    return sections
        .filter((section) => !section.hidden)
        .map((section) => ({ ...section, items: section.items }))
        .filter((section) => section.items.length > 0);
}

/**
 * Flat list of {label, path} for every nav item currently visible to the
 * user — used by the breadcrumb (path -> label) and the command palette
 * (static "Navegar" search results). Section label is kept alongside so the
 * breadcrumb can render "FlowVendas / Section / Page".
 */
export function useFlatNavItems() {
    const sections = useNavSections();
    const flat = [];
    sections.forEach((section) => {
        section.items.forEach((item) => {
            if (!item.path) return; // itens de ação (ex.: Assistente) não são rotas
            flat.push({ ...item, sectionLabel: section.label });
        });
    });
    return flat;
}

export default useNavSections;
